
import Vue from "vue";
import { mapActions, mapGetters } from "vuex";
import { GET_USER_DETAILS } from "@/store/modules/auth/constants";
import AiReportMetricCard from "@/components/candidate/interviews/ai/report/AiReportMetricCard.vue";
import AiReportInterviewDetailsCard from "@/components/candidate/interviews/ai/report/AiReportInterviewDetailsCard.vue";
import AiReportInsightsCard from "@/components/candidate/interviews/ai/report/AiReportInsightsCard.vue";
import { get_latest_interview_report } from "@/store/apis/ml_interview";
import {
  AI_INTERVIEW_INVITES,
  INIT_AI_INTERVIEW_INVITES
} from "@/store/modules/recruiter_ai_interviews/constants";

export default Vue.extend({
  name: "CandidateAiInterviewReport",
  components: {
    AiReportMetricCard,
    AiReportInterviewDetailsCard,
    AiReportInsightsCard
  },
  data() {
    return {
      botAvatar: require("@/assets/images/female-bot.png"),
      exportIcon: require("@/assets/images/interviews/export_pdf_icon.png"),
      dynamicRecommendationText: "",
      dynamicConfidenceText: "",
      dynamicFeedbackText: "",
      reportCandidateName: "",
      reportCandidateRole: "",
      overallScoreMetric: 0,
      questionsAnsweredMetric: 0,
      totalQuestionsMetric: 10,
      timeSpentSecondsMetric: 0,
      detailsTop: [
        { label: "Role", value: "Software Engineer" },
        { label: "Candidate", value: "Ahmad Al-Mansouri" },
        { label: "Experience Level", value: "8 Years" }
      ],
      detailsBottom: [
        { label: "Format", value: "Technical Interview" },
        { label: "Duration", value: "60 Minutes" },
        { label: "Interview Mode", value: "On Portal" }
      ],
      skills: ["HTML", "CSS", "SQL", "Python", "JavaScript", "C#", "Ruby", "Go"],
      insights: [
        { label: "Consistency", value: 89 },
        { label: "Growth Mindset", value: 79 },
        { label: "Ownership Language", value: 75 },
        { label: "Problem-Solving Depth", value: 74 },
        { label: "Cultural Alignment", value: 92 }
      ],
      questionAnalysis: [
        {
          question:
            "Welcome to your interview! I’m your AI interviewer. Let’s begin with the first question.",
          answer: "Thank you! I’m ready to begin."
        },
        {
          question:
            "Great! Can you tell me about your experience with the technologies relevant to this role?",
          answer:
            "I have been working with these technologies for about 2 years. I’ve built several projects including..."
        },
        {
          question:
            "Excellent answers throughout the interview. Let’s discuss your problem-solving approach.",
          answer:
            "I typically start by breaking down the problem into smaller components..."
        }
      ],
      technicalBreakdown: [] as Array<{ skill: string; score: number; evidence: string }>,
      behavioralPatternsList: [] as Array<{ pattern: string; type: string; evidence: string }>,
      consistencyFlagsList: [] as Array<{
        topic: string;
        resumeSays: string;
        candidateSaid: string;
        status: string;
      }>,
      strengthsList: [] as Array<{ title: string; evidence: string }>,
      developmentAreasList: [] as Array<{
        title: string;
        evidence: string;
        risk: string;
      }>,
      probesForHumanInterviewerList: [] as Array<{
        topic: string;
        reason: string;
        suggestedQuestion: string;
      }>
    };
  },
  async created() {
    await this.initInvites();
    await this.hydrateFromInterviewReport();
  },
  computed: {
    ...mapGetters("auth", {
      user: GET_USER_DETAILS
    }),
    ...mapGetters("recruiter_ai_interviews", {
      aiInvites: AI_INTERVIEW_INVITES
    }),
    candidateName(): string {
      if (!this.user?.first_name) return "Ahmad Al Mansouri";
      return `${this.user.first_name} ${this.user.last_name || ""}`.trim();
    },
    displayCandidateName(): string {
      return String(this.reportCandidateName || "").trim() || this.candidateName;
    },
    displayCandidateRole(): string {
      return String(this.reportCandidateRole || "").trim() || "Candidate";
    },
    timeSpentLabel(): string {
      const totalSeconds = Number(this.timeSpentSecondsMetric || 0);
      const minutes = Math.floor(totalSeconds / 60)
        .toString()
        .padStart(2, "0");
      const seconds = Math.floor(totalSeconds % 60)
        .toString()
        .padStart(2, "0");
      return `${minutes}:${seconds} Min`;
    },
    timeSpentProgress(): number {
      const totalSeconds = Number(this.timeSpentSecondsMetric || 0);
      const baselineSeconds = 20 * 60;
      return Math.min(100, Math.round((totalSeconds / baselineSeconds) * 100));
    },
    radarValues(): number[] {
      const getScore = (label: string) => {
        const found = (this.insights || []).find(
          (item: any) => String(item?.label || "").toLowerCase() === label.toLowerCase()
        );
        return Number(found?.value || 0);
      };
      return [
        getScore("Communication"),
        getScore("Technical Competency"),
        getScore("Behavioral"),
        getScore("Cultural Fit"),
        getScore("Overall")
      ].map((v) => Math.min(100, Math.max(0, Number(v || 0))));
    },
    radarAxisLines(): Array<{ x1: number; y1: number; x2: number; y2: number }> {
      const cx = 130;
      const cy = 130;
      const radius = 90;
      return [0, 1, 2, 3, 4].map((i) => {
        const angle = (-90 + i * 72) * (Math.PI / 180);
        return {
          x1: cx,
          y1: cy,
          x2: cx + radius * Math.cos(angle),
          y2: cy + radius * Math.sin(angle)
        };
      });
    },
    radarRings(): string[] {
      const cx = 130;
      const cy = 130;
      const radius = 90;
      const rings = [0.25, 0.5, 0.75, 1];
      return rings.map((ratio) =>
        [0, 1, 2, 3, 4]
          .map((i) => {
            const angle = (-90 + i * 72) * (Math.PI / 180);
            const x = cx + radius * ratio * Math.cos(angle);
            const y = cy + radius * ratio * Math.sin(angle);
            return `${x},${y}`;
          })
          .join(" ")
      );
    },
    radarValuePolygon(): string {
      const cx = 130;
      const cy = 130;
      const radius = 90;
      return this.radarValues
        .map((value, i) => {
          const ratio = value / 100;
          const angle = (-90 + i * 72) * (Math.PI / 180);
          const x = cx + radius * ratio * Math.cos(angle);
          const y = cy + radius * ratio * Math.sin(angle);
          return `${x},${y}`;
        })
        .join(" ");
    }
  }
  ,
  methods: {
    ...mapActions("recruiter_ai_interviews", {
      initInvites: INIT_AI_INTERVIEW_INVITES
    }),
    normalizeStrengthItem(item: any): { title: string; evidence: string } | null {
      if (typeof item === "string") {
        const text = String(item || "").trim();
        if (!text) return null;
        return { title: text, evidence: "" };
      }
      const title = String(item?.title || item?.name || "").trim();
      const evidence = String(item?.evidence || item?.detail || "").trim();
      if (!title && !evidence) return null;
      return { title: title || "Strength", evidence };
    },
    normalizeDevelopmentItem(item: any): { title: string; evidence: string; risk: string } | null {
      if (typeof item === "string") {
        const text = String(item || "").trim();
        if (!text) return null;
        return { title: text, evidence: "", risk: "" };
      }
      const title = String(item?.title || item?.name || "").trim();
      const evidence = String(item?.evidence || item?.detail || "").trim();
      const risk = String(item?.risk || "").trim();
      if (!title && !evidence && !risk) return null;
      return { title: title || "Development Area", evidence, risk };
    },
    isLikelyQuestion(text: string): boolean {
      const normalized = String(text || "").trim();
      if (!normalized) return false;
      if (normalized.includes("?")) return true;
      return /^(can|could|would|will|what|why|how|when|where|which|who|tell|describe|walk me)/i.test(
        normalized
      );
    },
    async hydrateFromInterviewReport() {
      try {
        const queryCandidateId = Number(this.$route.query.candidateId || 0);
        const queryJobId = Number(this.$route.query.jobId || 0);
        const candidateId = queryCandidateId || Number(this.user?.id || 0);
        if (!candidateId) return;
        const resp = await get_latest_interview_report(
          candidateId,
          "ai",
          queryJobId || null
        );
        const parsed = resp?.data?.data;
        if (!parsed) return;
        const aiReport = parsed?.ai_report || {};
        const meta = aiReport?._hcms_meta || {};
        const scores = aiReport?.scores || {};
        const overallScore = Number(aiReport?.overall_score || 0);
        const recommendation = String(aiReport?.recommendation || "review");
        this.technicalBreakdown = Array.isArray(aiReport?.technical_breakdown)
          ? aiReport.technical_breakdown.map((row: any) => ({
              skill: String(row?.skill || "Skill"),
              score: Number(row?.score || 0),
              evidence: String(row?.evidence || "")
            }))
          : [];
        this.behavioralPatternsList = Array.isArray(aiReport?.behavioral_patterns)
          ? aiReport.behavioral_patterns.map((row: any) => ({
              pattern: String(row?.pattern || ""),
              type: String(row?.type || ""),
              evidence: String(row?.evidence || "")
            }))
          : [];
        this.consistencyFlagsList = Array.isArray(aiReport?.consistency_flags)
          ? aiReport.consistency_flags.map((row: any) => ({
              topic: String(row?.topic || ""),
              resumeSays: String(row?.resume_says || ""),
              candidateSaid: String(row?.candidate_said || ""),
              status: String(row?.status || "")
            }))
          : [];
        this.strengthsList = Array.isArray(aiReport?.strengths)
          ? aiReport.strengths
              .map((s: any) => this.normalizeStrengthItem(s))
              .filter((v: any) => !!v)
          : [];
        this.developmentAreasList = Array.isArray(aiReport?.development_areas)
          ? aiReport.development_areas
              .map((s: any) => this.normalizeDevelopmentItem(s))
              .filter((v: any) => !!v)
          : [];
        this.probesForHumanInterviewerList = Array.isArray(aiReport?.probes_for_human_interviewer)
          ? aiReport.probes_for_human_interviewer.map((p: any) => ({
              topic: String(p?.topic || ""),
              reason: String(p?.reason || ""),
              suggestedQuestion: String(p?.suggested_question || "")
            }))
          : [];
        const transcriptItems = parsed?.ai_transcript || parsed?.transcript || [];
        const matchedInvite = (this.aiInvites || []).find((invite: any) => {
          const sameCandidate =
            Number(invite?.candidate_id || 0) === Number(candidateId || 0);
          if (!sameCandidate) return false;
          if (!queryJobId) return true;
          return Number(invite?.job_id || 0) === Number(queryJobId || 0);
        });
        this.reportCandidateName = String(matchedInvite?.candidate_name || "").trim();
        const matchedJobRole = String(matchedInvite?.job_role || "").trim();
        this.reportCandidateRole =
          (/^job\s*#\s*\d+$/i.test(matchedJobRole) ? "" : matchedJobRole) ||
          String(matchedInvite?.candidate_role || "").trim() ||
          "";
        const answeredFromTranscript = transcriptItems.filter((item: any) => {
          if (item?.speaker === "candidate") return true;
          if (item?.role === "user") return true;
          return false;
        }).length;
        const totalQuestions = Number(meta?.total_questions || 10);
        const answeredQuestions = Number(
          meta?.answered_questions ?? answeredFromTranscript
        );
        const hasMetaTimeSpent =
          meta && Object.prototype.hasOwnProperty.call(meta, "time_spent_seconds");
        const timeSpentFromMeta = Number(meta?.time_spent_seconds ?? 0);
        const estimatedFromTranscript = Math.max(0, answeredQuestions * 45);
        const timeSpentSeconds = hasMetaTimeSpent
          ? Math.max(0, timeSpentFromMeta)
          : estimatedFromTranscript;

        this.overallScoreMetric = overallScore;
        this.totalQuestionsMetric = totalQuestions;
        this.questionsAnsweredMetric = answeredQuestions;
        this.timeSpentSecondsMetric = timeSpentSeconds;

        this.insights = [
          { label: "Communication", value: Number(scores?.communication || 0) },
          {
            label: "Technical Competency",
            value: Number(scores?.technical_competency || 0)
          },
          { label: "Behavioral", value: Number(scores?.behavioral || 0) },
          { label: "Cultural Fit", value: Number(scores?.cultural_fit || 0) },
          { label: "Overall", value: overallScore }
        ];

        const mappedQuestions = transcriptItems
          .map((item: any, index: number, all: any[]) => {
            const isInterviewer =
              item?.role === "assistant" || item?.speaker === "interviewer";
            if (!isInterviewer) return null;
            const question = String(item?.content ?? item?.text ?? "").trim();
            if (!question) return null;
            if (!this.isLikelyQuestion(question)) return null;
            const next = all[index + 1];
            const nextIsCandidate =
              next &&
              (next?.role === "user" || next?.speaker === "candidate");
            const answer = nextIsCandidate
              ? String(next?.content ?? next?.text ?? "").trim() || "-"
              : "-";
            return { question, answer };
          })
          .filter((item: any) => !!item);
        this.questionAnalysis = mappedQuestions.slice(0, 10);

        this.detailsTop = [
          { label: "Role", value: this.displayCandidateRole || "Candidate" },
          { label: "Candidate", value: this.displayCandidateName },
          {
            label: "Experience Level",
            value: String(aiReport?.candidate_profile?.experience_match || "N/A")
          }
        ];
        this.detailsBottom = [
          { label: "Format", value: "AI Interview" },
          { label: "Duration", value: this.timeSpentLabel.replace(" Min", "") },
          { label: "Interview Mode", value: "On Portal" }
        ];

        const summary = aiReport?.executive_summary || "AI interview report generated.";
        const confidence = aiReport?.overall_score ? `${aiReport.overall_score}% Confidence` : "Pending";
        const titleText =
          recommendation.toLowerCase() === "shortlist"
            ? "Move to Technical Interview"
            : recommendation.toLowerCase() === "hire"
              ? "Recommended to Hire"
              : "Needs Review";
        (this as any).$set(this, "dynamicRecommendationText", titleText);
        (this as any).$set(this, "dynamicConfidenceText", confidence);
        (this as any).$set(this, "dynamicFeedbackText", summary);
      } catch (e) {
        // keep fallback static values
      }
    }
  }
});
